import React from 'react';
import { Line, Rect, SchematicShell } from './SchematicPrimitives';
import { wallSconceProportions } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Elevation schematic for `wall-light` — the uplight cone wall sconce.
 *
 * Matches `wallLightRenderer` in lightingRenderers.js; every number comes
 * from `WALL_SCONCE` / `wallSconceProportions` in proceduralSpecs.js. The
 * spec frame is y-up from the box bottom and z out from the wall; SVG y
 * grows downward, so y_svg = height - y_spec.
 *
 * Front: rectangular backplate seated at the bottom, a vertical stem, the
 * socket cup, and the cone shade (trapezoid, wide end up, rim at the top).
 * Side: backplate as a thin strip on the wall edge, the horizontal arm out
 * to the shade axis, the stem up, and the same cone profile — the mouth
 * spans from just off the wall to the front of the box. Right mirrors left.
 *
 * Registered for `wall-light` — the canonical sconce type emitted by
 * `vanity-assembly/createAccessories.js`. No variant fields.
 */
export function WallSconceSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const p = wallSconceProportions({ width, height, depth });
  const isSide = view === 'left' || view === 'right';
  const stroke = { strokeColor, strokeWidth };
  return (
    <SchematicShell
      width={isSide ? depth : width}
      height={height}
      drawFrame={false}
      {...stroke}
    >
      {isSide ? (
        <g
          transform={
            view === 'right' ? `translate(${depth}, 0) scale(-1, 1)` : undefined
          }
        >
          <SconceSide p={p} height={height} {...stroke} />
        </g>
      ) : (
        <SconceFront p={p} width={width} height={height} {...stroke} />
      )}
    </SchematicShell>
  );
}
WallSconceSchematic.supportsSideViews = true;

/** Cone shade + socket cup around a vertical axis at `cx`. */
function ShadeProfile({ p, cx, height, strokeColor, strokeWidth }) {
  const topY = height - p.shadeTopY;
  const bottomY = height - p.shadeBottomY;
  return (
    <>
      <polygon
        points={[
          [cx - p.shadeTopR, topY],
          [cx + p.shadeTopR, topY],
          [cx + p.shadeBottomR, bottomY],
          [cx - p.shadeBottomR, bottomY],
        ]
          .map(([x, y]) => `${x},${y}`)
          .join(' ')}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <Rect
        x={cx - p.shadeBottomR}
        y={bottomY - p.socketH}
        width={p.shadeBottomR * 2}
        height={p.socketH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </>
  );
}

function SconceFront({ p, width, height, strokeColor, strokeWidth }) {
  const cx = width / 2;
  const stroke = { strokeColor, strokeWidth };
  return (
    <>
      <Rect
        x={cx - p.backplateW / 2}
        y={height - p.backplateH}
        width={p.backplateW}
        height={p.backplateH}
        {...stroke}
      />
      {/* Stem: backplate centre (arm height) up to the socket cup. */}
      <Line
        x1={cx}
        y1={height - p.armY}
        x2={cx}
        y2={height - p.shadeBottomY}
        {...stroke}
      />
      <ShadeProfile p={p} cx={cx} height={height} {...stroke} />
    </>
  );
}

function SconceSide({ p, height, strokeColor, strokeWidth }) {
  const armY = height - p.armY;
  const stroke = { strokeColor, strokeWidth };
  return (
    <>
      {/* Backplate on the wall edge (x = 0). */}
      <Rect
        x={0}
        y={height - p.backplateH}
        width={p.backplateD}
        height={p.backplateH}
        {...stroke}
      />
      {/* Arm out to the shade axis, then the stem up to the cup. */}
      <Line
        x1={p.backplateD}
        y1={armY}
        x2={p.shadeAxisZ}
        y2={armY}
        {...stroke}
      />
      <Line
        x1={p.shadeAxisZ}
        y1={armY}
        x2={p.shadeAxisZ}
        y2={height - p.shadeBottomY}
        {...stroke}
      />
      <ShadeProfile p={p} cx={p.shadeAxisZ} height={height} {...stroke} />
    </>
  );
}
